// Shared request pipeline for the AI prompting suggest-edit endpoints.
//
// Extracted out of `./client.ts` so slice-specific client modules
// (`./clientDryRun.ts`, future expansions) can share the same Supabase
// access-token + X-Workspace-Organization-Id header behavior without
// re-implementing it OR creating an import cycle.

import { supabase } from "@/lib/supabase";
import { readWorkspaceOrgIdFromStorage } from "@/lib/workspaceOrgStorage";

export const OVERRIDES_ACTION = "/api/admin/action?action=ai-prompts-overrides";
export const SUGGESTIONS_ACTION = "/api/admin/action?action=ai-prompts-suggestions";
export const HISTORY_ACTION = "/api/admin/action?action=ai-prompts-history";
export const DRY_RUN_ACTION = "/api/admin/action?action=ai-prompts-dry-run";

export type Result<T> =
  | { ok: true; status: number; data: T }
  | { ok: false; status: number; error: string };

export async function call<T>(path: string, init: RequestInit = {}): Promise<Result<T>> {
  if (!supabase) return { ok: false, status: 503, error: "App is not configured for sign-in." };
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) return { ok: false, status: 401, error: "Sign in to manage AI prompts." };

  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
    ...(init.headers as Record<string, string>),
  };
  const ws = readWorkspaceOrgIdFromStorage();
  if (ws) headers["X-Workspace-Organization-Id"] = ws;

  const r = await fetch(path, { ...init, headers });
  const text = await r.text();
  let json: unknown = null;
  try {
    json = JSON.parse(text);
  } catch {
    /* non-JSON */
  }
  if (!r.ok) {
    const err =
      json && typeof json === "object" && "error" in json && typeof (json as { error: unknown }).error === "string"
        ? (json as { error: string }).error
        : text || `HTTP ${r.status}`;
    return { ok: false, status: r.status, error: err };
  }
  return { ok: true, status: r.status, data: json as T };
}
